import { ScheduleRecordProperty } from '@indigohive/cogfy-types'
import { formatDate } from 'date-fns/format'
import { isPast } from 'date-fns/isPast'
import { useEffect, useState } from 'react'
import { Button, Input, Select } from '../../../../components'
import { FieldOverlayProps } from '../../FieldOverlay'
import { isValid } from 'date-fns'

type Unit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months'

export function ScheduleOverlay (props: FieldOverlayProps) {
  const { record, field } = props
  const property = record.properties[field.id] as ScheduleRecordProperty | undefined
  const value = property?.schedule?.value

  const [startDateInput, setStartDateInput] = useState<string | null>(
    value?.startDate ? formatDate(new Date(value.startDate), "yyyy-MM-dd'T'HH:mm") : null
  )
  const startDateInputDate = startDateInput ? new Date(startDateInput) : null
  const [endDateInput, setEndDateInput] = useState<string | null>(
    value?.endDate ? formatDate(new Date(value.endDate), "yyyy-MM-dd'T'HH:mm") : null
  )

  const [interval, setInterval] = useState<number | null>(value?.interval ?? null)
  const [unit, setUnit] = useState<Unit | null>(value?.unit as Unit ?? null)
  const [maxOccurrences, setMaxOccurrences] = useState<number | null>(value?.maxOccurrences ?? null)

  const [updateEndDate, setUpdateEndDate] = useState<boolean>(value?.endDate !== null)
  const [updateMaxOccurrences, setUpdateMaxOccurrences] = useState<boolean>(value?.maxOccurrences !== null)

  const startDateError = startDateInputDate
    ? (isValid(startDateInputDate) && isPast(startDateInputDate))
    : false
  const endDateError = endDateInput
    ? (isValid(new Date(endDateInput)) && isPast(new Date(endDateInput)))
    : false
  const intervalError = interval !== null && (interval < 1 || interval > 99)
  const maxOccurrencesError = maxOccurrences !== null && (maxOccurrences < 1)

  useEffect(() => {
    if (startDateInput && interval && unit && !startDateError && !intervalError && !endDateError) {
      const startDate = new Date(startDateInput)
      const endDate = endDateInput ? new Date(endDateInput) : null

      if (isValid(startDate) && (!endDate || isValid(endDate))) {
        props.controller.onUpdateRecordProperties(
          props.record.id,
          {
            [props.field.id]: {
              type: 'schedule',
              schedule: {
                value: {
                  startDate: startDate.toISOString(),
                  nextDate: startDate.toISOString(),
                  interval,
                  unit,
                  endDate: endDate?.toISOString() ?? null,
                  maxOccurrences,
                  count: value?.count ?? 0
                }
              }
            }
          }
        )
      }
    }
  }, [startDateInput, endDateInput, interval, unit, maxOccurrences, startDateError, intervalError, endDateError])

  const handleClear = () => {
    setStartDateInput(null)
    setInterval(null)
    setUnit(null)
    props.controller.onUpdateRecordProperties(
      props.record.id,
      {
        [props.field.id]: {
          type: 'schedule',
          schedule: {
            value: null
          }
        }
      }
    )
  }

  return (
    <div
      className="menu-sm bg-base-100 rounded-btn border shadow-lg max-w-full flex flex-col gap-2 px-2 py-2"
      style={{ width: 250 }}
    >
      <Input
        size="sm"
        error={startDateError && !value?.startDate}
        disabled={!!value?.startDate}
        helperText={!value?.startDate && startDateError ? 'Start date must be in the future' : ''}
        value={startDateInput ?? ''}
        onChange={event => {
          setStartDateInput(event.target.value)
        }}
        label="Start date"
        type="datetime-local"
      />
      <Input
        size="sm"
        value={interval ?? ''}
        error={intervalError}
        helperText={intervalError ? 'Interval must be between 1 and 99' : undefined}
        onChange={event => {
          setInterval(Number(event.target.value) || null)
        }}
        label="Interval"
        type="number"
        min={1}
        max={99}
      />
      <Select
        size="md"
        value={unit || ''}
        onChange={event => {
          setUnit(event.target.value as Unit)
        }}
        options={[
          { label: '-', value: '' },
          { label: 'Minutes', value: 'minutes' },
          { label: 'Hours', value: 'hours' },
          { label: 'Days', value: 'days' },
          { label: 'Weeks', value: 'weeks' },
          { label: 'Months', value: 'months' }
        ]}
      />
      <Select
        size="md"
        value={updateEndDate ? 'custom' : ''}
        label="End date"
        onChange={event => {
          event.target.value === 'custom' ? setUpdateEndDate(true) : setUpdateEndDate(false)
          setEndDateInput(null)
        }}
        options={[
          { label: 'No end date', value: '' },
          { label: 'Custom', value: 'custom' }
        ]}
      />
      {updateEndDate && (
        <Input
          size="sm"
          error={endDateError}
          helperText={endDateError ? 'End date must be in the future' : ''}
          value={endDateInput ?? ''}
          onChange={event => {
            setEndDateInput(event.target.value)
          }}
          type="datetime-local"
        />
      )}
      <Select
        size="md"
        value={updateMaxOccurrences ? 'custom' : ''}
        label="Max occurrences"
        onChange={event => {
          event.target.value === 'custom' ? setUpdateMaxOccurrences(true) : setUpdateMaxOccurrences(false)
          setMaxOccurrences(null)
        }}
        options={[
          { label: 'No limit', value: '' },
          { label: 'Custom', value: 'custom' }
        ]}
      />
      {updateMaxOccurrences && (
        <Input
          size="sm"
          value={maxOccurrences ?? ''}
          error={maxOccurrencesError}
          helperText={maxOccurrencesError ? 'Max occurrences must be greater than 0' : ''}
          onChange={event => {
            setMaxOccurrences(Number(event.target.value))
          }}
          type="number"
          min={1}
        />
      )}
      <Button
        size="xs"
        onClick={() => handleClear()}
      >
        Clear
      </Button>
    </div>
  )
}
